/**
 * RATING — die Bewertung als mitlaufender CTA (Jans Skizze 27.09.2026):
 * oben die Frage, darunter GESTAPELTE Zeilen mit 1 … 5 Symbolen (Stern, Herz
 * oder Daumen — bis 07.10.2026 Kleeblatt). Ein Tipp auf eine Zeile gibt die
 * Bewertung ab.
 *
 * STUFE 1 wie bei VOTE: noch keine Zählung, der Leser merkt sich die Abgabe
 * nur auf dem Gerät; im Editor bedienbar mit ↺ zum Zurücksetzen.
 */
import { useEffect, useState } from "react";
import type { CSSProperties, FunctionComponent } from "react";
import type { Color, CtaAbgabe, RatingSymbol, ResolvedRating, ResolvedRunningCta } from "@scrolly/post-types";
// Gewählter Schnitt = Grundtext; Frage und Danke bleiben kräftiger, nie dünner
// (Jan 07.10.2026, s. schrift-staerke.ts)
import { betonterSchnitt } from "@scrolly/post-types";
import { ctaGeraeteId, leseGemerkt, merke, ZUKLAPPEN_NACH_MS } from "./ctaMerken";
import DankeZeile from "./DankeZeile";

const farbe = (c: Color, alpha = 1): string => `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a * alpha})`;

/** Die drei Symbole als eigene Vektoren — Emoji sähen auf jedem Gerät anders aus. */
const Symbol: FunctionComponent<{ art: RatingSymbol; voll: boolean; farbe: string }> = ({
    art,
    voll,
    farbe: f,
}) => {
    const strich = { fill: voll ? f : "none", stroke: f, strokeWidth: 1.8, strokeLinejoin: "round" as const };
    return (
        <svg viewBox="0 0 24 24" width="1.3em" height="1.3em" aria-hidden data-symbol={art} style={{ display: "block", flexShrink: 0 }}>
            {art === "heart" ? (
                <path
                    {...strich}
                    d="M12 20.5s-7.3-4.5-9.3-9C1.3 8.3 3.3 5 6.6 5c2 0 3.5 1.1 4.4 2.6.9-1.5 2.4-2.6 4.4-2.6 3.3 0 5.3 3.3 3.9 6.5-2 4.5-7.3 9-7.3 9z"
                />
            ) : art === "thumb" ? (
                // Daumen hoch statt Kleeblatt (Jan 07.10.2026): Hand mit Ärmel,
                // die Trennlinie zeigt den Ärmel auch im Umriss
                <g {...strich}>
                    <path d="M7 21H4a1.5 1.5 0 0 1-1.5-1.5v-8A1.5 1.5 0 0 1 4 10h3l3.6-6.2c.4-.7 1.2-1.1 2-1 1.3.2 2.1 1.4 1.8 2.7l-.8 4h5.7c1.3 0 2.3 1.2 2 2.5l-1.4 7.3c-.2 1-1.1 1.7-2.1 1.7z" />
                    <path d="M7 10v11" fill="none" />
                </g>
            ) : (
                <path {...strich} d="M12 2.6l2.8 6.1 6.6.7-4.9 4.5 1.4 6.5L12 17.1l-5.9 3.3 1.4-6.5-4.9-4.5 6.6-.7z" />
            )}
        </svg>
    );
};

interface Props {
    strip: ResolvedRunningCta;
    rating: ResolvedRating;
    editorMode?: boolean;
    /** Gesetzt = echter Leser: Abgabe merken und melden. Fehlt = Vorschau. */
    onCtaAntwort?: (abgabe: CtaAbgabe) => void;
}

const RunningRating: FunctionComponent<Props> = ({ strip, rating, editorMode, onCtaAntwort }) => {
    // Nur echte Leser merken sich die Abgabe (Vorschauen bleiben frisch)
    const echterLeser = !editorMode && Boolean(onCtaAntwort);
    const [wert, setWert] = useState<number | null>(null);
    const [zeige, setZeige] = useState<number | null>(null);
    // Nach der Abgabe kurz offen, dann zur schmalen Danke-Zeile (ZUKLAPPEN_NACH_MS)
    const [zu, setZu] = useState(false);

    useEffect(() => {
        if (!echterLeser) return;
        const gemerkt = leseGemerkt(strip.id);
        const n = gemerkt ? Number(gemerkt[0]) : NaN;
        if (Number.isFinite(n)) {
            setWert(n);
            setZu(true);
        }
    }, [echterLeser, strip.id]);

    const abgegeben = wert !== null;
    useEffect(() => {
        if (!abgegeben || zu) return undefined;
        const t = window.setTimeout(() => setZu(true), ZUKLAPPEN_NACH_MS);
        return () => window.clearTimeout(t);
    }, [abgegeben, zu]);
    const text = strip.textColor;
    const schrift: CSSProperties = strip.fontFamily ? { fontFamily: `'${strip.fontFamily}', sans-serif` } : {};

    const danke = rating.danke || "Danke fürs Mitmachen!";
    const zuruecksetzen = () => {
        setWert(null);
        setZu(false);
    };

    const bewerte = (n: number) => {
        if (abgegeben) return;
        setWert(n);
        if (echterLeser && onCtaAntwort) {
            merke(strip.id, [String(n)]);
            onCtaAntwort({ ctaId: strip.id, art: "rating", antwort: [String(n)], geraet: ctaGeraeteId() });
        }
    };

    if (zu) {
        return (
            <div style={{ width: "100%", color: farbe(text), fontSize: `${strip.fontSize}px`, ...schrift }}>
                <DankeZeile
                    text={danke}
                    staerke={betonterSchnitt(strip.fontWeight, 600)}
                    knopfGrund={farbe(text, 0.18)}
                    onZurueck={editorMode ? zuruecksetzen : undefined}
                />
            </div>
        );
    }

    return (
        <div
            style={{
                width: "100%",
                maxWidth: "560px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                color: farbe(text),
                fontSize: `${strip.fontSize}px`,
                lineHeight: 1.3,
                ...schrift,
            }}
        >
            {rating.frage ? (
                <div style={{ fontWeight: betonterSchnitt(strip.fontWeight, 700), paddingRight: "28px" }}>{rating.frage}</div>
            ) : null}

            <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
                onMouseLeave={() => setZeige(null)}
            >
                {rating.zeilen.map((n) => {
                    // Gewählte Zeile voll, beim Überfahren die Zeile unter dem Zeiger
                    const an = abgegeben ? wert === n : zeige === n;
                    return (
                        <button
                            key={n}
                            type="button"
                            aria-label={`${n} / ${rating.stufen}`}
                            onClick={() => bewerte(n)}
                            onMouseEnter={() => !abgegeben && setZeige(n)}
                            disabled={abgegeben}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                width: "100%",
                                minHeight: "36px",
                                padding: "4px 12px",
                                color: "inherit",
                                background: an ? farbe(text, 0.22) : farbe(text, 0.06),
                                border: `1.5px solid ${farbe(text, an ? 0.9 : 0.3)}`,
                                borderRadius: "8px",
                                cursor: abgegeben ? "default" : "pointer",
                                opacity: abgegeben && !an ? 0.45 : 1,
                            }}
                        >
                            {Array.from({ length: n }, (_, i) => (
                                <Symbol key={i} art={rating.symbol} voll={an} farbe={farbe(text)} />
                            ))}
                        </button>
                    );
                })}
            </div>

            {abgegeben ? (
                <div style={{ display: "flex", alignItems: "center", gap: "10px", fontWeight: betonterSchnitt(strip.fontWeight, 600) }}>
                    <span>{danke}</span>
                    {editorMode ? (
                        <button
                            type="button"
                            title="Bewertung zurücksetzen (nur im Editor)"
                            onClick={zuruecksetzen}
                            style={{
                                border: "none",
                                background: farbe(text, 0.18),
                                color: "inherit",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                cursor: "pointer",
                                padding: 0,
                            }}
                        >
                            ↺
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default RunningRating;
